import { useCallback, useMemo } from 'react';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';

// ==============================|| RETURN NAVIGATION - înapoi la ruta de ORIGINE ||============================== //
// Paginile de creare/editare/detalii primesc locația completă de origine (pathname +
// query + hash) prin `navigate(..., { state: { from } })` și se întorc la EA — poate fi
// lista modulului cu filtre/paginare, dar la fel de bine oricare altă rută internă
// (ex. /administrations/15?tab=users → /users/create → înapoi la administrație).
// Nu ne bazăm pe navigate(-1): pagina poate fi deschisă direct, dintr-un refresh sau
// în tab nou — atunci cădem pe `returnTo` din query (dacă există) sau pe fallback.
//
// Ordinea de rezolvare a țintei de întoarcere:
//   1. `location.state.from` valid și intern — navigarea normală din aplicație;
//   2. parametrul de query `returnTo` valid și intern — DOAR pentru fluxurile care
//      trebuie să supraviețuiască refresh-ului / link-urilor copiate / tab-urilor noi
//      (se adaugă explicit cu withReturnTo, nu implicit pe fiecare rută);
//   3. fallback-ul configurat al paginii.

/**
 * Doar căi INTERNE: încep cu un singur '/' (un '//' e URL protocol-relative către alt
 * host — refuzat). `state.from` și `returnTo` sunt controlabile de utilizator, nu
 * navigăm orbește.
 */
export const isSafeInternalPath = (value: unknown): value is string =>
	typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

/** Bucățile de locație necesare helper-elor — structural, ca să rămână funcții pure. */
export interface InternalLocationParts {
	pathname: string;
	search: string;
	hash: string;
}

export interface ResolvableLocation extends InternalLocationParts {
	state?: unknown;
}

/** Locația internă COMPLETĂ (pathname + query + hash) — valoarea de pus în `state.from`. */
export const buildCurrentLocation = (location: InternalLocationParts): string =>
	`${location.pathname}${location.search}${location.hash}`;

const RETURN_TO_KEY = 'returnTo';

/**
 * Rezolvă ținta de întoarcere pentru locația curentă, în ordinea documentată mai sus.
 * Pură (primește locația ca date) — paginile intermediare o pot folosi ca să
 * FORWARDEZE originea inițială mai departe, în loc s-o înlocuiască cu ele însele.
 */
export function resolveOriginPath(
	currentLocation: ResolvableLocation,
	fallbackPath: string
): string {
	const stateFrom = (currentLocation.state as { from?: unknown } | null | undefined)
		?.from;
	if (isSafeInternalPath(stateFrom)) return stateFrom;

	const returnToParam = new URLSearchParams(currentLocation.search).get(RETURN_TO_KEY);
	if (isSafeInternalPath(returnToParam)) return returnToParam;

	return fallbackPath;
}

/**
 * Atașează originea ca `returnTo` în query — pentru link-urile care trebuie să
 * supraviețuiască refresh-ului / copierii / deschiderii în tab nou (state-ul de router
 * se pierde acolo). NU se folosește implicit la fiecare navigare — doar unde e nevoie.
 */
export function withReturnTo(targetPath: string, origin: string): string {
	if (!isSafeInternalPath(origin)) return targetPath;
	const separator = targetPath.includes('?') ? '&' : '?';
	return `${targetPath}${separator}${RETURN_TO_KEY}=${encodeURIComponent(origin)}`;
}

export interface UseReturnNavigationOptions {
	/** Ruta de întoarcere când pagina a fost deschisă fără origine (direct/refresh). */
	fallbackPath: string;
}

export interface ReturnNavigation {
	/** Ținta de întoarcere rezolvată: state.from → ?returnTo → fallback. */
	returnPath: string;
	navigateBack: () => void;
	/**
	 * `state`-ul de atașat la navigarea către o pagină următoare: forwardează originea
	 * INIȚIALĂ (nu pagina curentă), ca lanțurile listă → detalii → editare să se
	 * întoarcă tot la origine.
	 */
	forwardState: { from: string };
}

export function useReturnNavigation(
	options: UseReturnNavigationOptions | string
): ReturnNavigation {
	const fallbackPath = typeof options === 'string' ? options : options.fallbackPath;
	const location = useLocation();
	const navigate = useNavigate();

	const returnPath = useMemo(
		() => resolveOriginPath(location, fallbackPath),
		[location, fallbackPath]
	);

	const navigateBack = useCallback(() => navigate(returnPath), [navigate, returnPath]);

	return {
		returnPath,
		navigateBack,
		forwardState: { from: returnPath }
	};
}

/**
 * Navigare cu origine: pasează locația curentă completă atât prin `state.from`
 * (navigarea normală în aplicație), cât și prin `?returnTo=` în URL-ul țintei.
 * Dublarea e deliberată: history state-ul NU supraviețuiește redirect-urilor de
 * autentificare (roundtrip-ul Keycloak recreează intrarea de istoric), refresh-urilor
 * forțate sau tab-urilor noi — în acele cazuri originea se recuperează din `returnTo`.
 */
export function useNavigateWithOrigin(): (
	targetPath: string,
	options?: Omit<NavigateOptions, 'state'>
) => void {
	const location = useLocation();
	const navigate = useNavigate();

	return useCallback(
		(targetPath, options) => {
			const origin = buildCurrentLocation(location);
			navigate(withReturnTo(targetPath, origin), {
				...options,
				state: { from: origin }
			});
		},
		[navigate, location]
	);
}
